import { apiBlobRequest, apiRequest, type BlobDownload } from '../api/http'
import type {
  Product,
  ProductCreateRequest,
  ProductImagePresignRequest,
  ProductImagePresignResponse,
  ProductListItem,
  ProductListParams,
  ProductOptions,
  ProductOptionUpdateRequest,
  ProductPage,
  ProductStockExportParams,
  ProductUpdateRequest,
  ProductZohoRefreshResponse,
} from './types'

const PRODUCTS_BASE = '/api/products'

function productAuth(accessToken: string) {
  return { accessToken }
}

function productQuery(params: ProductListParams = {}): string {
  const search = new URLSearchParams()
  if (params.page != null) search.set('page', String(params.page))
  if (params.size != null) search.set('size', String(params.size))
  if (params.query?.trim()) search.set('query', params.query.trim())
  if (params.status) search.set('status', params.status)
  if (params.listedProvider) search.set('listedProvider', params.listedProvider)
  if (params.listedChannelConnectionId != null) search.set('listedChannelConnectionId', String(params.listedChannelConnectionId))
  const query = search.toString()
  return query ? `?${query}` : ''
}

function stockExportQuery(params: ProductStockExportParams = {}): string {
  return productQuery(params)
}

export function listProducts(accessToken: string, params: ProductListParams = {}): Promise<ProductPage<ProductListItem>> {
  return apiRequest<ProductPage<ProductListItem>>(PRODUCTS_BASE, productQuery(params), productAuth(accessToken))
}

export function exportProductsStock(accessToken: string, params: ProductStockExportParams = {}): Promise<BlobDownload> {
  return apiBlobRequest(PRODUCTS_BASE, `/stock/export${stockExportQuery(params)}`, productAuth(accessToken))
}

export function getProduct(accessToken: string, id: number): Promise<Product> {
  return apiRequest<Product>(PRODUCTS_BASE, `/${id}`, productAuth(accessToken))
}

export function getProductOptions(accessToken: string, id: number): Promise<ProductOptions> {
  return apiRequest<ProductOptions>(PRODUCTS_BASE, `/${id}/options`, productAuth(accessToken))
}

export function updateProductOption(accessToken: string, id: number, optionId: number, body: ProductOptionUpdateRequest): Promise<ProductOptions> {
  return apiRequest<ProductOptions>(PRODUCTS_BASE, `/${id}/options/${optionId}`, { method: 'PUT', body, accessToken })
}

export function dismissProductOptionsReleased(accessToken: string, id: number): Promise<Product> {
  return apiRequest<Product>(PRODUCTS_BASE, `/${id}/options-released/dismiss`, { method: 'POST', accessToken })
}

export function deactivateProductAfterOptionsReleased(accessToken: string, id: number): Promise<Product> {
  return apiRequest<Product>(PRODUCTS_BASE, `/${id}/options-released/deactivate`, { method: 'POST', accessToken })
}

export function createProduct(accessToken: string, payload: ProductCreateRequest): Promise<Product> {
  return apiRequest<Product>(PRODUCTS_BASE, '', { method: 'POST', body: payload, accessToken })
}

export function updateProduct(accessToken: string, id: number, payload: ProductUpdateRequest): Promise<Product> {
  return apiRequest<Product>(PRODUCTS_BASE, `/${id}`, { method: 'PUT', body: payload, accessToken })
}

export function deleteProduct(accessToken: string, id: number): Promise<void> {
  return apiRequest<void>(PRODUCTS_BASE, `/${id}`, { method: 'DELETE', accessToken })
}

export function refreshProductFromZoho(accessToken: string, id: number): Promise<ProductZohoRefreshResponse> {
  return apiRequest<ProductZohoRefreshResponse>(PRODUCTS_BASE, `/${id}/zoho/refresh`, { method: 'POST', accessToken })
}

export function presignProductImage(accessToken: string, request: ProductImagePresignRequest): Promise<ProductImagePresignResponse> {
  return apiRequest<ProductImagePresignResponse>(PRODUCTS_BASE, '/images/presign', { method: 'POST', body: request, accessToken })
}

export function deleteProductImageObject(accessToken: string, key: string): Promise<void> {
  return apiRequest<void>(PRODUCTS_BASE, `/images?key=${encodeURIComponent(key)}`, { method: 'DELETE', accessToken })
}
